import { NgModule } from '@angular/core';
import {
  PreloadAllModules, RouterModule, Routes, UrlMatchResult, UrlSegment,
} from '@angular/router';

import { ForceVerificationSetupGuard } from '@firestitch/2fa';
import { AclAccess } from '@firestitch/acl';

import { CardComponent } from '@libs/layouts/card';
import { SideNavComponent } from '@libs/layouts/side-nav';

import {
  AdminGuard,
  ConversationsRedirectGuard,
  EnrolmentRedirectGuard,
  FormSubmissionsGuard,
  LoggedInGuard, LoggedOutGuard,
  OrganizationGuard,
  PermissionGuard,
  SystemGuard,
} from '@app/common/guards';
import { AllReportPermissions } from '@common/consts';
import { AclPermission } from '@common/enums';

import { TermsGuard } from './terms';


export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./home').then((m) => m.HomeModule),
  },
  {
    path: '',
    component: CardComponent,
    children: [
      {
        path: 'signin',
        loadChildren: () => import('@libs/signin').then((m) => m.SigninModule),
        canActivate: [LoggedOutGuard],
      },
    ],
  },
  {
    path: '',
    component: CardComponent,
    children: [
      {
        // Old reset links and bookmarks; Forgot password lives on the sign-in screen (TU-T9623 R43)
        path: 'password',
        redirectTo: '/signin',
      },
      {
        path: 'passwordchange',
        loadChildren: () => import('./password-change').then((m) => m.PasswordChangeModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'invite',
        loadChildren: () => import('./invite').then((m) => m.InviteModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'activation',
        loadChildren: () => import('./activation').then((m) => m.ActivationModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'registration',
        loadChildren: () => import('./registration').then((m) => m.RegistrationModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'waitlistconfirm',
        loadChildren: () => import('./registration-waitlist-confirmation').then((m) => m.RegistrationWaitlistConfirmationModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'donation',
        loadChildren: () => import('./donation').then((m) => m.DonationModule),
        data: { bodyClass: 'body-card' },
      },
      {
        matcher: (url: UrlSegment[]): UrlMatchResult | null => {
          if (url.length === 4 &&
              url[0].path === 'forms' &&
                url[2].path === 'form' &&
                  url[1].path.match(/^[0-9a-z]+$/i) &&
                    url[3].path.match(/^[0-9a-z]+$/i)) {

            return {
              consumed: url,
              posParams: {
                guid: new UrlSegment(url[1].path, {}),
                formSubmission: new UrlSegment(url[3].path, {}),
              },
            };
          }

          return null;
        },
        redirectTo: 'form/:guid/:formSubmission',
      },
      {
        matcher: (url: UrlSegment[]): UrlMatchResult | null => {
          const guid = String(url[1]?.path || '');

          if (url[0].path === 'forms' && guid.match(/[0-9]/) && guid.match(/[a-z]/i)) {
            url[0].path = 'form';

            return ({
              consumed: url.slice(0, 2),
              posParams: {
                guid: new UrlSegment(guid, {}),
              },
            });
          }

          return null;
        },
        redirectTo: 'form/:guid',
      },
      {
        path: 'form',
        loadChildren: () => import('./form').then((m) => m.FormModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'verification',
        loadChildren: () => import('@firestitch/2fa').then((m) => m.FsVerificationSetupModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'offline',
        loadChildren: () => import('./offline').then((m) => m.OfflineModule),
        data: { bodyClass: 'body-card' },
      },
      {
        path: 'processes',
        loadChildren: () => import('./processes').then((m) => m.ProcessesModule),
      },
      {
        path: 'terms',
        loadChildren: () => import('./terms').then((m) => m.TermsModule),
        data: { bodyClass: 'body-card' },
      },
    ],
  },
  {
    path: '',
    canActivate: [TermsGuard, ForceVerificationSetupGuard, LoggedInGuard],
    children: [
      {
        path: '',
        component: SideNavComponent,
        children: [
          {
            path: 'account',
            loadChildren: () => import('./account').then((m) => m.AccountModule),
          },
          {
            path: 'parent',
            loadChildren: () => import('./parent').then((m) => m.ParentModule),
          },
          {
            path: 'conversations',
            canActivate: [ConversationsRedirectGuard],
            loadChildren: () => import('./conversations').then((m) => m.ConversationsModule),
          },
          { path: 'field', loadChildren: () => import('./field').then((m) => m.FieldModule) },
          {
            path: '',
            canActivate: [OrganizationGuard],
            children: [
              {
                path: 'dashboard',
                canActivate: [PermissionGuard],
                loadChildren: () => import('./dashboard').then((m) => m.DashboardModule),
                data: {
                  fsNav: { root: true },
                  permission: AclPermission.Invoicetransaction,
                },
              },
              {
                path: 'parents',
                loadChildren: () => import('./parents').then((m) => m.ParentsModule),
              },
              {
                path: 'organization-forms/:formId/submissions/:formSubmissionId',
                canActivate: [FormSubmissionsGuard],
                children: [],
              },
              {
                path: 'forms',
                loadChildren: () => import('./forms').then((m) => m.FormsModule),
              },
              {
                path: 'organization',
                canActivate: [PermissionGuard],
                data: {
                  permission: AclPermission.Organization,
                  access: AclAccess.Write,
                },
                loadChildren: () => import('./environment').then((m) => m.EnvironmentModule),
              },
              {
                path: 'invoices',
                loadChildren: () => import('./invoices').then((m) => m.InvoicesModule),
              },
              {
                path: 'donations',
                loadChildren: () => import('./donation-invoices').then((m) => m.DonationInvoicesModule),
              },
              { path: 'enrollments', loadChildren: () => import('./enrolments').then((m) => m.EnrolmentsModule) },
              { path: 'students', loadChildren: () => import('./students').then((m) => m.StudentsModule) },
              { path: 'registrations', loadChildren: () =>
                import('./registrations').then((m) => m.RegistrationsModule) },
              {
                path: 'programs',
                loadChildren: () =>
                  import('./programs/programs.module')
                    .then((m) => m.ProgramsModule),
              },
              {
                path: 'attendance',
                loadChildren: () => import('./attendance').then((m) => m.AttendanceModule),
              },
              {
                path: 'reports',
                canActivate: [PermissionGuard],
                data: {
                  permission: AllReportPermissions,
                  access: AclAccess.Read,
                },
                loadChildren: () => import('./reports').then((m) => m.ReportsModule),
              },
            ],
          },
          {
            path: 'admin',
            loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
            canActivate: [AdminGuard],
          },
          {
            path: 'system',
            loadChildren: () => import('./system/system.module').then((m) => m.SystemModule),
            canActivate: [SystemGuard],
          },
          {
            path: 'notfound',
            loadChildren: () => import('@libs/not-found/not-found.module').then((m) => m.NotFoundModule),
          },
        ],
      },
    ],
  },
  {
    path: 'blank',
    component: CardComponent,
  },
  {
    path: '**',
    canActivate: [EnrolmentRedirectGuard],
    component: CardComponent,
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(
      routes,
      {
        preloadingStrategy: PreloadAllModules,
      },
    ),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
